'use client';

import Link from 'next/link';
import { coverBackground } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { LearningRailFrame } from './_LearningRailFrame';
import { ContentRail } from '../ui/ContentRail';
import { cardReset } from '../_shared/styles';

/**
 * Fixed-cardinality "Top 10" rail — Netflix-style oversized rank
 * numerals beside each item.
 */
export interface Top10RowProps {
  title?: string;
  source?: string;
  seeAllLabel?: string;
  seeAllHref?: string;
  courseHrefTemplate?: string;
}

const SOURCE_ALIAS: Record<string, string> = {
  popular: 'popular_courses',
  featured: 'featured_courses',
  newest: 'newly_published_courses',
  trending: 'popular_courses',
};

export function Top10Row({
  title,
  source: rawSource,
  seeAllLabel,
  seeAllHref,
  courseHrefTemplate = '/lxp/course/{id}',
}: Top10RowProps) {
  const trimmed = (rawSource ?? '').trim() || 'popular_courses';
  const source = SOURCE_ALIAS[trimmed] ?? trimmed;
  return (
    <LearningRailFrame
      blockType="Top10Row"
      source={source}
      limit={10}
      title={title}
      seeAllLabel={seeAllLabel}
      seeAllHref={seeAllHref}
      emptyTitle="Nothing in the Top 10 yet"
      emptyHint="Rankings populate as the catalog gets traffic."
    >
      {(items) => (
        <ContentRail ariaLabel={title || 'Top 10'} snap="start">
          {items.slice(0, 10).map((item, i) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 8,
                flex: '0 0 320px',
                minWidth: 320,
              }}
            >
              <RankNumeral n={i + 1} />
              <Card asChild>
                <Link
                  href={courseHrefTemplate.replace('{id}', encodeURIComponent(item.id))}
                  style={{
                    ...cardReset,
                    flex: 1,
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'var(--color-surface-card, #fff)',
                    borderRadius: 'var(--radius-md, 8px)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    aria-hidden
                    style={{ aspectRatio: '16 / 10', ...coverBackground(item.imageUrl) }}
                  />
                  <div
                    style={{
                      padding: 12,
                      fontSize: 'var(--font-size-sm, 14px)',
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {item.title}
                  </div>
                </Link>
              </Card>
            </div>
          ))}
        </ContentRail>
      )}
    </LearningRailFrame>
  );
}

function RankNumeral({ n }: { n: number }) {
  return (
    <div
      aria-hidden
      style={{
        fontSize: 'clamp(80px, 12vw, 120px)',
        fontWeight: 900,
        lineHeight: 0.9,
        color: 'var(--color-surface-card, #fff)',
        WebkitTextStroke: '2px var(--color-text-primary, #111)',
        fontFamily: 'var(--font-family-display, inherit)',
        flex: '0 0 auto',
        alignSelf: 'flex-end',
        userSelect: 'none',
      }}
    >
      {n}
    </div>
  );
}
